import React, { useContext, useEffect, useRef } from 'react';
import { useDrop } from 'react-dnd';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import BuilderContext from 'common/Context';
import I18n from 'modules/i18n';
import checkSymbol from 'images/icons/check.svg';
import ItemTypes from '../ItemTypes';
import { backgroundStyle, matchDropzonesToTextAreas } from '../utils';
import DraggableChoiceContainer from './DraggableChoiceEditor/Container';
import doSnapToGrid from './snapToGrid';
import TextArea from './TextArea';
import styles from './DragAndDrop.module.scss';
import './froala.scss';
import TextAreaToolbar from './TextArea/Toolbar';
import DropZoneWithDraggableChoices from './DropZoneWithDraggableChoices';

const saveStatusText = (isSaving, saveFailed) => {
  if (isSaving) return `${I18n.t('saving')}…`;
  if (saveFailed) return I18n.t('drag_drop_not_saved');

  return I18n.t('saved');
};

const Container = ({
  background,
  correctAnswers,
  draggableChoices,
  dropZones,
  isSaving,
  saveFailed,
  shouldHideGrid,
  snapToGrid,
  textAreas,
}) => {
  const boundsRef = useRef();
  const backgroundRef = useRef();
  const {
    dropZoneDispatch,
    setBackgroundRef,
    questionId,
    textAreaDispatch,
    vocabLinkOption
  } = useContext(BuilderContext);
  const minDimensions = { height: 5, width: 5 };

  const textAreaDragHandler = (item, left, top) => {
    textAreaDispatch({ payload: { ...item, left, top }, type: 'UPDATE' });
    if (vocabLinkOption === 'images') {
      const nextTextAreas = { ...textAreas, [item.id]: { ...textAreas[item.id], left, top } };
      const matched = matchDropzonesToTextAreas(dropZones, nextTextAreas, vocabLinkOption);
      dropZoneDispatch({ payload: matched, type: 'SET_MATCHED' });
    }
  };

  const [, drop] = useDrop(() => ({
    accept: [ItemTypes.BOX, ItemTypes.textArea],
    drop(item, monitor) {
      const delta = monitor.getDifferenceFromInitialOffset();

      // percents of the parent params
      // Using the parent for height, slowly desyncs the item off of the grid, but
      // using the child's height value keeps it aligned
      const parentHeight = boundsRef.current.firstChild.clientHeight / 100;
      const parentWidth = boundsRef.current.clientWidth / 100;

      let left;
      let top;

      if (snapToGrid) {
        left = Math.round((item.left * parentWidth) + delta.x);
        top = Math.round((item.top * parentHeight) + delta.y);
      }
      else {
        // this is the x coordinate of the left side of the draggable
        // need to convert this to a percent by dividing it by the width of the parent element
        left = ((item.left * parentWidth) + delta.x) / parentWidth;

        // this is the y coordinate of the top side of the draggable
        // need to convert this to a percent by dividing it by the height of the parent element
        top = ((item.top * parentHeight) + delta.y) / parentHeight;
      }

      if (snapToGrid) {
        [left, top] = doSnapToGrid(left, top);
        // convert values to percentages after the snapping happens
        left /= parentWidth;
        top /= parentHeight;
      }
      if (item.type === ItemTypes.BOX) {
        dropZoneDispatch({ payload: { ...item, left, top }, type: 'UPDATE' });
      } else {
        textAreaDragHandler(item, left, top);
      }
      return undefined;
    },
  }), [dropZoneDispatch, draggableChoices, dropZones, textAreas]);

  useEffect(() => {
    setBackgroundRef(backgroundRef);
  }, [backgroundRef]);

  const renderDropZones = () => (
    Object.keys(dropZones).sort().map(key => (
      <DropZoneWithDraggableChoices
        id={key}
        key={`DropZoneWithDraggableChoices-${key}`}
        boundsRef={boundsRef}
        correctAnswers={correctAnswers[key] || []}
        draggableChoices={draggableChoices}
        minDimensions={minDimensions}
        dropZoneProps={dropZones[key]}
      />
    ))
  );

  const renderTextAreas = () => (
    Object.keys(textAreas).sort().map(key => (
      <TextArea
        key={key}
        id={key}
        boundsRef={boundsRef}
        minDimensions={minDimensions}
        {...textAreas[key]}
      />
    ))
  );

  return (
    <div
      className={styles.dropZonesWrapper}
      ref={boundsRef}
    >
      <div className={styles.containerWrapper}>
        <section
          aria-label="Drop Zones"
          ref={drop}
          className={styles.dropZones}
        >
          <div
            className={styles.bgContainer}
            ref={backgroundRef}
            style={backgroundStyle(background)}
          >
            <div className={clsx(styles.gridDisplay, { [styles.gridLines]: !shouldHideGrid })} />
            <div className={clsx(styles.saveStatus, { [styles.saveFailed]: saveFailed && !isSaving })}>
              {!isSaving && !saveFailed && <img alt="Saving" className={styles.saveSymbol} src={checkSymbol} />}
              <div className={styles.saveText}>
                {saveStatusText(isSaving, saveFailed)}
              </div>
            </div>
            {renderDropZones()}
            {renderTextAreas()}
          </div>
        </section>
        <TextAreaToolbar questionId={questionId} />
      </div>
      <DraggableChoiceContainer draggableChoices={draggableChoices} />
    </div>
  );
};

Container.propTypes = {
  background: PropTypes.shape({
    id: PropTypes.number,
    position: PropTypes.string,
    url: PropTypes.string,
  }).isRequired,
  correctAnswers: PropTypes.objectOf(PropTypes.array).isRequired,
  draggableChoices: PropTypes.objectOf(PropTypes.object).isRequired,
  dropZones: PropTypes.objectOf(PropTypes.object).isRequired,
  isSaving: PropTypes.bool.isRequired,
  saveFailed: PropTypes.bool,
  shouldHideGrid: PropTypes.bool,
  snapToGrid: PropTypes.bool.isRequired,
  textAreas: PropTypes.objectOf(PropTypes.object)
};

Container.defaultProps = {
  saveFailed: false,
  shouldHideGrid: false,
  textAreas: {}
};

export default Container;
